<template>
  <div
    class="form__div"
    :class="{ 'has-value': isPrefilled || hasInputValue || isFocused }"
  >
    <vue-tel-input
      ref="phoneInputRef"
      class="form__input"
      id="phoneNumber"
      v-model="phoneValue"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      :default-country="'US'"
      :inputOptions="{ placeholder: props.placeholder }"
      :autoFormat="true"
      :enabledCountryCode="true"
      :enabledFlags="true"
      :mode="'international'"
      :formatOnDisplay="true"
      :validCharactersOnly="true"
      :value="modelValue"
    />
    <label for="phoneNumber" class="form__label text-label">Phone number</label>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, onMounted, ref, watch } from "vue";

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: "Phone number" },
});

const emit = defineEmits(["update:modelValue", "onValidation"]);

const phoneInputRef = ref(null);
const phoneValue = ref(props.modelValue || "");
const hasInputValue = ref(false);
const isFocused = ref(false);
const isPrefilled = ref(false);

const checkInputValue = () => {
  if (!phoneInputRef.value) return;

  const input = phoneInputRef.value.$el.querySelector("input");
  if (!input) return;

  hasInputValue.value = !!input.value && input.value.trim().length > 0;

  const container = phoneInputRef.value.$el;
  if (!container) return;

  if (hasInputValue.value || isFocused.value) {
    container.classList.add("vti__is-filled");
  } else {
    container.classList.remove("vti__is-filled");
  }
};

const handleInput = (_phone, phoneObject) => {
  if (!phoneObject) return;

  const input = phoneInputRef.value.$el.querySelector("input");
  let visualValue = input?.value || "";

  const hasInvalidChars = /[^0-9+]/.test(visualValue);

  const isValid = Boolean(
    phoneObject?.valid && phoneObject?.number && !hasInvalidChars
  );

  emit("update:modelValue", phoneObject?.number);
  emit("onValidation", isValid);
  checkInputValue();
};

const handleFocus = () => {
  isFocused.value = true;
  if (!phoneInputRef.value) return;

  const container = phoneInputRef.value.$el;
  if (container) {
    container.classList.add("vti__is-filled");
  }
};

const handleBlur = () => {
  isFocused.value = false;
  checkInputValue();
};

const blockInvalidChars = (event) => {
  const input = event.target;
  const value = input.value;

  if (event.ctrlKey || event.metaKey || event.altKey || event.key.length > 1) {
    return;
  }

  if (event.key === "+") {
    if (value.length === 0) return;
    else event.preventDefault();
    return;
  }

  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== phoneValue.value) {
      phoneValue.value = newValue || "";
      checkInputValue();
    }
    isPrefilled.value = Boolean(newValue);
  },
  { immediate: true }
);

onMounted(() => {
  isPrefilled.value = phoneValue.value && phoneValue.value.length > 0;
  checkInputValue();

  if (phoneInputRef.value) {
    const input = phoneInputRef.value.$el.querySelector("input");
    if (input) {
      input.addEventListener("keypress", blockInvalidChars);
    }
  }
});
</script>

<style scoped>
:deep(.vue-tel-input) {
  position: relative;
  z-index: 50 !important;
  width: 100% !important;
  background-color: #ecf0f5 !important;
  border: 3px solid transparent !important;
  border-radius: 0.5rem !important;
  padding: 0.1rem 0.1rem;
  display: flex !important;
  flex-wrap: nowrap !important;
  transition: all 0.3s ease !important;
  outline: none !important;
}

:deep(.vti__input) {
  background-color: transparent !important;
  border: none !important;
  padding-left: 1rem !important;
  outline: none !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #000 !important;
}

:deep(.vti__input::placeholder) {
  color: #81848d !important;
}

:deep(.vti__dropdown) {
  background-color: transparent !important;
  border: none !important;
  opacity: 0;
  width: 0 !important;
  padding: 0 !important;
  outline: none !important;
}

:deep(.vti__dropdown-list) {
  width: 300px !important;
  max-width: 90vw !important;
  left: 0 !important;
  right: auto !important;
}

:deep(.vti__dropdown-item) {
  width: 100% !important;
  padding: 8px 12px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

:deep(.vti__is-filled .vti__dropdown) {
  opacity: 1 !important;
  width: auto !important;
  padding-right: 8px !important;
}

:deep(.vti__is-filled .vti__input) {
  padding-left: 8px !important;
}

.form__div:focus-within :deep(.vue-tel-input),
:deep(.vue-tel-input.vti__is-filled),
.form__div.has-value :deep(.vue-tel-input) {
  background-color: white !important;
  border: 3px solid #00355d !important;
  border-radius: 0% !important;
  box-shadow: 0 0 10px rgba(0, 53, 93, 0.2) !important;
  outline: none !important;
}

.form__div:focus-within :deep(.vti__dropdown),
.form__div.has-value :deep(.vti__dropdown) {
  opacity: 1;
  width: auto !important;
  padding-right: 8px !important;
}

.form__div:focus-within :deep(.vti__input),
.form__div.has-value :deep(.vti__input) {
  padding-left: 8px !important;
}

.form__div:focus-within :deep(.vti__input::placeholder) {
  color: transparent !important;
}

label[for="phoneNumber"].form__label {
  z-index: 88 !important;
  opacity: 0;
  position: absolute;
  left: 1rem;
  top: 0.7rem;
  padding: 0 0.25rem;
  background-color: white;
  transition: all 0.4s ease;
}

.form__div:focus-within label[for="phoneNumber"].form__label,
:deep(.vti__is-filled) ~ label[for="phoneNumber"].form__label,
.form__div.has-value label[for="phoneNumber"].form__label {
  z-index: 88 !important;
  opacity: 1;
  top: -0.5rem !important;
  left: 0.8rem !important;
  font-size: 13px !important;
  font-weight: 500 !important;
}
:deep(.vti__dropdown-item) strong {
  font-weight: 400 !important;
}
</style>
